import type {ReactNode} from "react";
import {cn} from "@/lib/utils.ts";
import {srcUrl} from "@/api/ImageApi.ts";
import type {Image} from "@/images/Image.ts";
import type {TokenFrameStyle} from "@/encounter/model/TokenFrameStyle.ts";
import question from "@assets/question.svg";
import frameShadow from "@assets/token-frame-shadow.webp";
import {TOKEN_PORTRAIT_RADIUS_RATIO, tokenFrameImage} from "./TokenFrameStyles.ts";

/** HTML token artwork shared by the frame picker and initiative portraits. */
export default function TokenFramePreview({style, image, portraitSource, className, health = 1, children}: {
    style: TokenFrameStyle; image?: Image; portraitSource?: string; className: string;
    health?: number; children?: ReactNode;
}) {
    const portraitSize = `${TOKEN_PORTRAIT_RADIUS_RATIO * 100}%`;
    const remainingHealth = Number.isFinite(health) ? Math.max(0, Math.min(1, health)) : 1;
    return <span aria-hidden="true" className={cn("relative block aspect-square shrink-0", className)}>
        {/* Clip the portrait and rising lost-health fill beneath the decorative frame. */}
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full bg-white"
            style={{width: portraitSize, height: portraitSize}}>
            <img src={portraitSource ?? (image ? srcUrl(image) : question)} alt="" draggable={false}
                className="size-full object-contain"/>
            {remainingHealth < 1 && <span className="absolute inset-x-0 bottom-0 bg-[#640000]/60"
                style={{height: `${(1 - remainingHealth) * 100}%`}}/>}
        </span>
        {/* The inner shadow recesses the portrait beneath the frame artwork. */}
        <img src={frameShadow} alt="" draggable={false} className="absolute inset-0 size-full object-contain"/>
        <img src={tokenFrameImage(style)} alt="" draggable={false} className="relative size-full object-contain"/>
        {/* Foreground ornaments supplied by the consuming view stay above the frame. */}
        {children}
    </span>;
}
